import { useEffect, useRef, useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";

// Helpers
// MUI
import { Box, Button, Divider, Grid, Stack, Typography } from "@mui/material";

// Images
import empty from "@/assets/images/commons/empty.png";

// Hooks
import { useSubscriptions } from "@/Common/context/subscriptions/Subscriptions";
import { useAuthorization } from "@/Common/hooks/Authorization/Authorization";
import { useAuthorizationGroupsAccessPaginatedQuery } from "@Settings/hooks/useAuthorizationGroupsAccessPaginatedQuery";

// API's
import {
	addUserGroup,
	addUserGroupAccessActions,
	addUserGroupAccessUsers,
	deleteUserGroup,
	deleteUserGroupAccessActions,
	deleteUserGroupAccessUsers,
	updateUserGroup,
} from "../../api/GroupAccessSettings/mutations";

// Constants
import actions from "@/Constants/actionsList";

// Components
import GroupAccessView from "./GroupAccessView";
import ContentScrollable from "@/Layouts/components/ContentScrollable";
import EmptyBody from "@/Layouts/components/Basics/EmptyBody";
import ScrollSideBar from "@/Layouts/components/SideBars/ScrollSideBar/ScrollSideBar";
import { LoadingSpinner } from "@Layouts/components/LoadingSpinner";

// Helpers
import { addItemToInfiniteCache, deleteItemFromInfiniteCache, updateItemInInfiniteCache } from "@Common/hooks/Pagination/helpers";

// Types
import { IGroupSettings } from "@Settings/types/GroupSettings/models";
import { GraphqlSubscriptionResponse } from "@Common/api/types/GraphqlSubscriptionResponse";
import { IAccessAction } from "@Auth/types/models/action";
import { ILocation } from "@Common/interfaces/location";
import { IUser } from "@Common/interfaces/user";

type TOnAddUserGroup = Pick<IGroupSettings, "_id" | "groupName" | "groupDescription" | "locationId"> & {
	groupActions: IAccessAction[];
};

type TGroupAccessSettingsProps = {
	location: ILocation;
	selectedLocation: ILocation | null;
};

const getIndividualData = (data: IGroupSettings[] = [], index = "") => data.find((element) => element._id === index);

function orderGroupsBy(groups: IGroupSettings[] = [], orderBy: keyof IGroupSettings = "groupName"): IGroupSettings[] {
	const copyGroups = groups.slice();

	copyGroups.sort((itemA, itemB) => {
		if (typeof itemA[orderBy] === "string") {
			return itemA[orderBy].localeCompare(itemB[orderBy] as string);
		} else {
			if (itemA[orderBy] > itemB[orderBy]) {
				return 1;
			}
			return itemA[orderBy] < itemB[orderBy] ? -1 : 0;
		}
	});

	const { old: allOldGroups, new: allNewGroup } = copyGroups.reduce(
		(acc, menu) => {
			if (menu.groupName === "New Group") {
				acc.new.push(menu);
			} else {
				acc.old.push(menu);
			}
			return acc;
		},
		{ old: [], new: [] } as { old: IGroupSettings[]; new: IGroupSettings[] }
	);

	return [...allOldGroups, ...allNewGroup];
}

export default function GroupAccessSettingsModel(props: TGroupAccessSettingsProps) {
	const { selectedLocation = null } = props;

	const subscriptionId = useRef<string | null>(null);
	const [location, setLocation] = useState<ILocation | null>(null);
	const [selected, setSelected] = useState<IGroupSettings | null>(null);
	const [updating, setUpdating] = useState<string | null>(null);
	const [addAmount, setAddAmount] = useState(1);

	const [deleting, setDeleting] = useState({
		action: false,
		user: false,
	});

	const [updatingGroup, setUpdatingGroup] = useState<string | null>(null);

	const { getSubscription } = useSubscriptions();
	const queryClient = useQueryClient();
	const { useAuth } = useAuthorization();
	const editable = useAuth(actions.EDIT_ACTION_GROUPS);

	/********************************************* QUERIES ************************************************/
	const { data: groups, isLoading, isFetching } = useAuthorizationGroupsAccessPaginatedQuery({ location: location as ILocation });

	/********************************************* SUBSCRIPTIONS ************************************************/
	const groupAccessSettingsSubscription = getSubscription(["groupAccessSettings", selectedLocation?.locationId]);

	groupAccessSettingsSubscription?.subscriptions?.onAddUserGroup?.setCallback(
		(result: GraphqlSubscriptionResponse<"onAddUserGroup", TOnAddUserGroup>) => {
			const newUserGroup = result.data?.onAddUserGroup;

			const { _id, locationId, groupName, groupDescription, groupActions } = newUserGroup ?? {};
			const finalUserGroup = { _id, locationId, groupName, groupDescription, actions: groupActions, users: [] };

			if (newUserGroup) {
				addItemToInfiniteCache(queryClient, ["GroupSettingAccess", locationId], finalUserGroup);
			}
		}
	);

	groupAccessSettingsSubscription?.subscriptions?.onUpdateUserGroup.setCallback(
		(result: GraphqlSubscriptionResponse<"onUpdateUserGroup", IGroupSettings>) => {
			const updatedUserGroup = result.data?.onUpdateUserGroup;
			const locationId = groupAccessSettingsSubscription.getLastKeyIdentifier();

			if (updatedUserGroup) {
				updateItemInInfiniteCache<IGroupSettings>(
					queryClient,
					["GroupSettingAccess", locationId],
					(cachedGroup) => cachedGroup._id === updatedUserGroup?._id,
					() => updatedUserGroup
				);
			}

			if (subscriptionId.current === locationId) {
				setUpdatingGroup(null);
			}
		}
	);

	groupAccessSettingsSubscription?.subscriptions?.onDeleteUserGroup.setCallback(
		(result: GraphqlSubscriptionResponse<"onDeleteUserGroup", IGroupSettings>) => {
			const deletedUserGroup = result.data?.onDeleteUserGroup;
			const locationId = groupAccessSettingsSubscription.getLastKeyIdentifier();

			if (deletedUserGroup) {
				deleteItemFromInfiniteCache<IGroupSettings>(
					queryClient,
					["GroupSettingAccess", locationId],
					(cachedGroup) => cachedGroup._id === deletedUserGroup?._id
				);
			}

			if (subscriptionId.current === locationId) {
				setUpdatingGroup(null);
			}
		}
	);

	groupAccessSettingsSubscription?.subscriptions?.onAddUserGroupAccessActions.setCallback(
		(result: GraphqlSubscriptionResponse<"onAddUserGroupAccessActions", IGroupSettings>) => {
			const newUserGroupActions = result.data?.onAddUserGroupAccessActions;
			const locationId = groupAccessSettingsSubscription.getLastKeyIdentifier();

			if (newUserGroupActions) {
				updateItemInInfiniteCache<IGroupSettings>(
					queryClient,
					["GroupSettingAccess", locationId],
					(cachedGroup) => cachedGroup._id === newUserGroupActions?._id,
					(cachedGroup) => {
						return {
							...cachedGroup,
							actions: [...(cachedGroup.actions ?? []), ...newUserGroupActions.actions],
						};
					}
				);

				setUpdating(null);
			}

			if (subscriptionId.current === locationId) {
				setUpdating(null);
			}
		}
	);

	groupAccessSettingsSubscription?.subscriptions?.onDeleteUserGroupAccessActions.setCallback(
		(result: GraphqlSubscriptionResponse<"onDeleteUserGroupAccessActions", IGroupSettings>) => {
			const userGroup = result.data?.onDeleteUserGroupAccessActions;
			const locationId = groupAccessSettingsSubscription.getLastKeyIdentifier();

			if (userGroup) {
				updateItemInInfiniteCache<IGroupSettings>(
					queryClient,
					["GroupSettingAccess", locationId],
					(cachedGroup) => cachedGroup._id === userGroup?._id,
					(cachedGroup) => {
						const actionsToBeRemoved = userGroup.actions.map((action) => action._id);
						return {
							...cachedGroup,
							actions: cachedGroup.actions.filter((action) => !actionsToBeRemoved.includes(action._id)),
						};
					}
				);
			}

			if (subscriptionId.current === locationId) {
				setUpdating(null);
				setDeleting({ action: false, user: false });
			}
		}
	);

	groupAccessSettingsSubscription?.subscriptions?.onAddUserGroupAccessUsers.setCallback(
		(result: GraphqlSubscriptionResponse<"onAddUserGroupAccessUsers", IGroupSettings>) => {
			const userGroup = result.data?.onAddUserGroupAccessUsers;
			const locationId = groupAccessSettingsSubscription.getLastKeyIdentifier();

			if (userGroup) {
				updateItemInInfiniteCache<IGroupSettings>(
					queryClient,
					["GroupSettingAccess", locationId],
					(cachedGroup) => cachedGroup._id === userGroup?._id,
					(cachedGroup) => {
						const usersToAdd = userGroup.users;

						return {
							...cachedGroup,
							users: [...cachedGroup.users, ...usersToAdd],
						};
					}
				);
			}

			if (subscriptionId.current === locationId) {
				setUpdating(null);
			}
		}
	);

	groupAccessSettingsSubscription?.subscriptions?.onDeleteUserGroupAccessUsers.setCallback(
		(result: GraphqlSubscriptionResponse<"onDeleteUserGroupAccessUsers", IGroupSettings>) => {
			const userGroup = result.data?.onDeleteUserGroupAccessUsers;
			const locationId = groupAccessSettingsSubscription.getLastKeyIdentifier();

			if (userGroup) {
				updateItemInInfiniteCache<IGroupSettings>(
					queryClient,
					["GroupSettingAccess", locationId],
					(cachedGroup) => cachedGroup._id === userGroup?._id,
					(cachedGroup) => {
						const usersToBeRemoved = userGroup?.users.map((user) => user._id);
						return {
							...cachedGroup,
							users: cachedGroup.users.filter((user) => !usersToBeRemoved.includes(user._id)),
						};
					}
				);
			}

			if (subscriptionId.current === locationId) {
				setUpdating(null);
				setDeleting({ action: false, user: false });
			}
		}
	);

	/********************************************* MUTATIONS ************************************************/
	const { mutate: addGroup, isPending: addingGroup } = useMutation({
		mutationFn: (input: { locationId: string; authorizedLocation: string }) =>
			addUserGroup(input?.locationId, input?.authorizedLocation),
	});

	const { mutate: updateGroup } = useMutation({
		mutationFn: (input: Pick<IGroupSettings, "_id" | "locationId" | "groupName" | "groupDescription">) =>
			updateUserGroup(props.location.locationId, input),
	});

	const { mutate: deleteGroup } = useMutation({
		mutationFn: (input: { locationId: string; groupId: string; authorizedLocation: string }) =>
			deleteUserGroup(input.locationId, input.groupId, input.authorizedLocation),
	});

	const { mutate: addActions, isPending: addingActions } = useMutation({
		mutationFn: (userGroupActions: Pick<IGroupSettings, "_id" | "locationId"> & { actions: string[] }) =>
			addUserGroupAccessActions(props.location.locationId, userGroupActions),
	});

	const { mutate: deleteActions } = useMutation({
		mutationFn: (userGroupActions: Pick<IGroupSettings, "_id" | "locationId"> & { actions: string[] }) =>
			deleteUserGroupAccessActions(props.location.locationId, userGroupActions),
	});

	const { mutate: addUsers, isPending: addingUsers } = useMutation({
		mutationFn: (userGroupUsers: Pick<IGroupSettings, "_id" | "locationId"> & { users: string[] }) =>
			addUserGroupAccessUsers(props.location.locationId, userGroupUsers),
	});

	const { mutate: deleteUsers } = useMutation({
		mutationFn: (userGroupUsers: Pick<IGroupSettings, "_id" | "locationId"> & { users: string[] }) =>
			deleteUserGroupAccessUsers(props.location.locationId, userGroupUsers),
	});

	const handleClick = (group: IGroupSettings) => {
		if (group?._id !== selected?._id) {
			setSelected(group);
		}
	};

	const handleAddActions = (actions: IAccessAction[] = [], index: string, locationId: string) => {
		const userGroupActions = {
			_id: index,
			actions: actions.map((item) => item.actionKey),
			locationId,
		};
		addActions(userGroupActions);
		setAddAmount(actions.length);
		setUpdating(index);
	};

	const handleDeleteActions = (actions: IAccessAction[] = [], index: string, locationId: string) => {
		if (actions?.length && index) {
			const userGroupActions = {
				_id: index,
				actions: actions.map((item) => item.actionKey),
				locationId,
			};

			deleteActions(userGroupActions);
			setUpdating(index);
			setDeleting({ ...deleting, action: true });
		}
	};

	const handleAddUsers = (users: IUser[] = [], index: string, locationId: string) => {
		if (users?.length && index) {
			const userGroupUsers = {
				_id: index,
				users: users.map((item) => item._id ?? ""),
				locationId,
			};

			addUsers(userGroupUsers);
			setAddAmount(users.length);
			setUpdating(index);
		}
	};

	const handleDeleteUsers = (users: IUser[] = [], index: string, locationId: string) => {
		if (users?.length && index) {
			const userGroupUsers = {
				_id: index,
				users: users.map((item) => item._id ?? ""),
				locationId,
			};

			deleteUsers(userGroupUsers);
			setUpdating(index);
			setDeleting({ ...deleting, user: true });
		}
	};

	const handleAddGroup = () => {
		addGroup({ locationId: props.location.locationId, authorizedLocation: location?.locationId as string });
	};

	const handleUpdateGroup = (name: string, description: string, index: string, locationId: string) => {
		const input = {
			locationId,
			_id: index,
			groupName: name,
			groupDescription: description,
		};

		setUpdatingGroup(index);
		updateGroup(input);
	};

	const handleDeleteGroup = (groupId: string, locationId: string) => {
		const input = {
			locationId: props.location?.locationId,
			authorizedLocation: locationId,
			groupId,
		};

		setUpdatingGroup(groupId);
		deleteGroup(input);
	};

	useEffect(() => {
		if (!props.selectedLocation) {
			return;
		}

		setLocation(props.selectedLocation);
		subscriptionId.current = props.selectedLocation.locationId;

		return () => {
			subscriptionId.current = null;
			setLocation(null);
			setSelected(null);
			setUpdating(null);
			setAddAmount(1);
			setUpdatingGroup(null);
		};
	}, [props.selectedLocation]);

	if (!location) {
		return (
			<EmptyBody
				image={empty}
				title={"Select a location to view their Group Access"}
				bodyHeight={"500px"}
				sx={{
					boxShadow: "none !important",
				}}
			/>
		);
	}

	return (
		<Grid container spacing={4}>
			<Grid item xs={2.5}>
				<ScrollSideBar
					title={"Groups"}
					position={"sticky"}
					top={"120px"}
					menus={orderGroupsBy((groups as IGroupSettings[]) || [])}
					selected={selected?._id}
					showBy={"groupName"}
					orderBy={"groupName"}
					scrollBy={"_id"}
					loading={isLoading}
					adding={addingGroup}
					deleting={updatingGroup}
					defaultSelection
					addProperties={{
						title: "Add Group",
						isClearComponent: true,
						component: (
							<Button
								fullWidth
								variant="text"
								disableRipple
								disableFocusRipple
								disableElevation
								disabled={!editable}
								className="!justify-start !capitalize hover:!bg-transparent disabled:opacity-60 disabled:cursor-default"
								onClick={handleAddGroup}
							>
								<Typography className="regular-pt-sans-pro-narrow-label underline !text-primary">Add Group</Typography>
							</Button>
						),
					}}
					onActionClick={(group) => handleClick(group)}
				/>
			</Grid>

			<Grid item xs={8.5}>
				<Stack spacing={2}>
					{!isLoading && isFetching && (
						<Box component={"span"} pt={0.25} mb={2}>
							<LoadingSpinner label={`Group access settings data still loading...`} className="items-center" />
						</Box>
					)}
					<ContentScrollable scrollTo={selected?._id}>
						{addingGroup && (
							<Grid container spacing={2} mb={1}>
								<GroupAccessView loading={true} />
								<Grid container justifyContent={"center"}>
									<Grid item xs={7}>
										<Divider className="!border-disabled" sx={{ margin: "50px 0px 40px" }} />
									</Grid>
								</Grid>
							</Grid>
						)}

						{isLoading && <GroupAccessView loading={true} />}

						{!isLoading && (
							<Grid item container>
								{orderGroupsBy(groups as IGroupSettings[]).map((element, key) => {
									return (
										<GroupAccessView
											key={`${element._id}_${element.groupName}_${key}`}
											index={element._id}
											title={element.groupName}
											description={element.groupDescription}
											divider={Boolean((groups?.length ?? 0) > 1 && groups?.length !== key + 1)}
											adding={{
												actions: addingActions && updating === element._id,
												users: addingUsers && updating === element._id,
											}}
											deleting={deleting}
											loading={updatingGroup === element._id}
											addAmount={addAmount}
											data={getIndividualData(groups as IGroupSettings[], element._id)}
											locationId={element.locationId}
											isEditable={editable}
											// Callbacks
											onAddActions={handleAddActions}
											onDeleteActions={handleDeleteActions}
											onAddUsers={handleAddUsers}
											onDeleteUsers={handleDeleteUsers}
											onDeleteGroup={() => handleDeleteGroup(element._id, element.locationId)}
											onUpdateGroup={handleUpdateGroup}
										/>
									);
								})}
							</Grid>
						)}
					</ContentScrollable>
				</Stack>
			</Grid>
		</Grid>
	);
}
